import type { Norma, SearchHit, Source } from '../api/types';

type Listener<T> = (value: T) => void;

class Signal<T> {
  private value: T;
  private listeners = new Set<Listener<T>>();
  constructor(initial: T) {
    this.value = initial;
  }
  get(): T {
    return this.value;
  }
  set(next: T): void {
    if (Object.is(this.value, next)) return;
    this.value = next;
    for (const l of this.listeners) l(next);
  }
  update(fn: (current: T) => T): void {
    this.set(fn(this.value));
  }
  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    listener(this.value);
    return () => this.listeners.delete(listener);
  }
}

export type ViewMode = 'html' | 'md';

export interface RecentItem {
  source: Source;
  id: string;
  ori?: 'L' | 'D';
  numero: string;
  tipo: string;
  titulo?: string;
  visitedAt: number;
  favorite?: boolean;
}

const RECENTS_KEY = 'visor.recents.v1';
const THEME_KEY = 'visor.theme.v1';

function loadRecents(): RecentItem[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: RecentItem[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Partial<RecentItem> & Record<string, unknown>;
      const source = rec.source === 'nacion' || rec.source === 'cordoba' || rec.source === 'bcra' ? rec.source : null;
      const id = typeof rec.id === 'string' ? rec.id : '';
      if (!source || !id) continue;
      out.push({
        source,
        id,
        ori: rec.ori === 'L' || rec.ori === 'D' ? rec.ori : undefined,
        numero: String(rec.numero ?? ''),
        tipo: String(rec.tipo ?? ''),
        titulo: rec.titulo == null ? undefined : String(rec.titulo),
        visitedAt: typeof rec.visitedAt === 'number' ? rec.visitedAt : Date.now(),
        favorite: Boolean(rec.favorite),
      });
    }
    return out;
  } catch {
    return [];
  }
}

function saveRecents(items: RecentItem[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(items.slice(0, 50)));
  } catch {}
}

function loadTheme(): 'light' | 'dark' {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const store = {
  activeTab: new Signal<Source>('nacion'),
  query: new Signal<string>(''),
  searchResults: new Signal<SearchHit[]>([]),
  searching: new Signal<boolean>(false),
  searchError: new Signal<string | null>(null),
  indexUpdatedAt: new Signal<number | null>(null),

  currentNorma: new Signal<Norma | null>(null),
  loadingNorma: new Signal<boolean>(false),
  normaError: new Signal<string | null>(null),

  viewMode: new Signal<ViewMode>('html'),
  mdCache: new Map<string, string>(),

  recents: new Signal<RecentItem[]>(loadRecents()),
  theme: new Signal<'light' | 'dark'>(loadTheme()),

  drawerOpen: new Signal<boolean>(false),

  pushRecent(item: Omit<RecentItem, 'visitedAt'>): void {
    const list = this.recents.get();
    const key = `${item.source}:${item.id}`;
    const existing = list.find((i) => `${i.source}:${i.id}` === key);
    const favorite = existing?.favorite ?? item.favorite ?? false;
    const next: RecentItem[] = [
      { ...item, favorite, visitedAt: Date.now() },
      ...list.filter((i) => `${i.source}:${i.id}` !== key),
    ];
    this.recents.set(next);
    saveRecents(next);
  },

  toggleFavorite(source: Source, id: string): void {
    const list = this.recents.get();
    const next = list.map((i) =>
      i.source === source && i.id === id ? { ...i, favorite: !i.favorite } : i,
    );
    this.recents.set(next);
    saveRecents(next);
  },

  removeRecent(source: Source, id: string): void {
    const next = this.recents.get().filter((i) => !(i.source === source && i.id === id));
    this.recents.set(next);
    saveRecents(next);
  },

  setTheme(theme: 'light' | 'dark'): void {
    this.theme.set(theme);
    localStorage.setItem(THEME_KEY, theme);
    document.documentElement.dataset.theme = theme;
  },
};

document.documentElement.dataset.theme = store.theme.get();
